import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { tasksService } from '../services/tasksService';
import { taskPaymentsService } from '../services/taskPaymentsService';
import { clientsService } from '../services/clientsService';
import { objectsService } from '../services/objectsService';
import { toast } from '../utils/toast';
import { normalizeDueDateForSubmit } from '../utils/normalizeDueDateForSubmit';
import { taskServerId } from '../utils/taskServerId';
import { roundMoney2 } from '../utils/money';
import TaskForm from '../components/TaskForm';
import TaskWaitingDialog from '../components/TaskWaitingDialog';
import { TASK_COLORS } from '../components/TaskForm';
import { formatWaitingDate, waitingReasonLabel } from '../utils/taskWaiting';
import { isPersonalTask } from '../utils/taskKind';
import {
  DEFAULT_TASK_SCHEDULE,
  buildTaskDueDate,
  taskDateKey,
  taskDueDateForForm,
  taskScheduleFormFields,
} from '../utils/taskSchedule';
import './Tasks.css';

const getDateKey = (date) => {
  const storedKey = taskDateKey(date);
  if (storedKey) return storedKey;
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatTaskDate = (dueDate) => {
  const key = taskDateKey(dueDate);
  if (!key) return '';
  const [year, month, day] = key.split('-');
  return `${day}.${month}.${year}`;
};

const Tasks = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const objectTaskHandled = useRef(false);
  const linkedTaskHandled = useRef(false);
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [completingTaskId, setCompletingTaskId] = useState(null);
  const [paymentCreatingTaskId, setPaymentCreatingTaskId] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [waitingTask, setWaitingTask] = useState(null);
  const [waitingSaving, setWaitingSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client_id: '',
    object_id: '',
    due_date: '',
    ...DEFAULT_TASK_SCHEDULE,
    priority: 'medium',
    cost: 0,
    category: '',
    color: TASK_COLORS[1].value,
    address: '',
    carry_items: [],
    status: 'in_progress',
    is_recurring: false,
    recurrence_type: '',
    expenses: [],
    is_personal: false,
  });

  const loadTasks = useCallback(async () => {
    try {
      const response = await tasksService.getAll();
      let list = [];
      if (response) {
        if (Array.isArray(response)) list = response;
        else if (response.data) list = Array.isArray(response.data) ? response.data : [];
      }
      setTasks(list);
    } catch (error) {
      console.error('Error loading tasks:', error);
      toast.error('Ошибка загрузки задач');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadClients = useCallback(async () => {
    try {
      const response = await clientsService.getAll();
      setClients(response.data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  }, []);

  const loadObjects = useCallback(async () => {
    try { setObjects(await objectsService.getAll({ status: 'active' })); }
    catch (error) { console.error('Error loading objects:', error); }
  }, []);

  useEffect(() => {
    void loadTasks();
    void loadClients();
    void loadObjects();
    const handleFocus = () => {
      void loadTasks();
    };
    window.addEventListener('focus', handleFocus);
    window.addEventListener('crm:sync-completed', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('crm:sync-completed', handleFocus);
    };
  }, [loadTasks, loadClients, loadObjects]);

  const displayStatus = (task) => ((task.status === 'pending' || task.status === 'cancelled') ? 'in_progress' : task.status);
  const activeTasks = tasks.filter((t) => t && (t.id != null || t.server_id != null) && !['completed', 'waiting'].includes(displayStatus(t)));
  const waitingTasks = tasks.filter((t) => t && (t.id != null || t.server_id != null) && displayStatus(t) === 'waiting');
  const unpaidTasks = tasks.filter((task) => task
    && displayStatus(task) === 'completed'
    && Number(task.cost || 0) > 0
    && (task.payment_status === 'unpaid' || task.is_paid === false || task.is_paid == null));

  const todayKey = getDateKey(new Date());
  const tomorrowDate = new Date();
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrowKey = getDateKey(tomorrowDate);

  const missed = activeTasks.filter((t) => t.due_date && getDateKey(t.due_date) < todayKey);
  const today = activeTasks.filter((t) => t.due_date && getDateKey(t.due_date) === todayKey);
  const tomorrow = activeTasks.filter((t) => t.due_date && getDateKey(t.due_date) === tomorrowKey);
  const someday = activeTasks.filter((t) => !t.due_date);
  const later = activeTasks.filter((t) => t.due_date && getDateKey(t.due_date) > tomorrowKey);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      client_id: '',
      object_id: '',
      due_date: '',
      ...DEFAULT_TASK_SCHEDULE,
      priority: 'medium',
      cost: 0,
      category: '',
      color: TASK_COLORS[1].value,
      address: '',
      carry_items: [],
      status: 'in_progress',
      is_recurring: false,
      recurrence_type: '',
      expenses: [],
      is_personal: false,
    });
  };

  useEffect(() => {
    if (objectTaskHandled.current || searchParams.get('new') !== '1' || objects.length === 0) return;
    const linkedObject = objects.find(object => String(object.id) === String(searchParams.get('object_id')));
    if (!linkedObject) return;
    objectTaskHandled.current = true;
    setEditingTask(null);
    setFormData({
      title: '', description: '', client_id: linkedObject.client_id || '', object_id: linkedObject.id,
      due_date: '', priority: 'medium', cost: 0, category: '', color: TASK_COLORS[1].value,
      ...DEFAULT_TASK_SCHEDULE,
      address: linkedObject.address || '', carry_items: [], status: 'in_progress', is_recurring: false,
      recurrence_type: '', expenses: [],
      is_personal: false,
    });
    setShowModal(true);
    setSearchParams({}, { replace: true });
  }, [objects, searchParams, setSearchParams]);

  const handleSubmit = async (data) => {
    try {
      const submitData = { ...data };
      let savedTask = null;
      normalizeDueDateForSubmit(submitData);
      if (editingTask) {
        const id = taskServerId(editingTask);
        if (!submitData.status) submitData.status = editingTask.status || 'in_progress';
        if (submitData.status === 'pending' || submitData.status === 'cancelled') submitData.status = 'in_progress';
        savedTask = await tasksService.update(id, submitData);
      } else {
        submitData.status = 'in_progress';
        savedTask = await tasksService.create(submitData);
      }
      const queued = savedTask?.sync_status === 'pending'
        || savedTask?.queuedForSync === true
        || savedTask?.offline === true;
      toast.success(queued
        ? 'Задача сохранена на телефоне и синхронизируется при появлении сети'
        : (editingTask ? 'Задача обновлена' : 'Задача создана'));
      setShowModal(false);
      setEditingTask(null);
      resetForm();
      if (queued) {
        const local = await tasksService.getLocalTasks();
        setTasks(local || []);
      } else {
        await loadTasks();
      }
      navigate('/tasks', { replace: true });
      return true;
    } catch (error) {
      console.error('Error saving task:', error);
      if (error.queuedForSync) {
        toast.success('Сервер недоступен. Задача сохранена и будет отправлена при восстановлении связи.');
        setShowModal(false);
        setEditingTask(null);
        resetForm();
        await tasksService.getLocalTasks().then((local) => setTasks(local || []));
        navigate('/tasks', { replace: true });
        return true;
      } else {
        const msg = error.response?.status === 502 || error.response?.status === 503 || error.response?.status === 504
          ? 'Сервер временно недоступен. Попробуйте позже.'
          : (error.response?.data?.error || 'Ошибка при сохранении задачи');
        toast.error(msg);
        return false;
      }
    }
  };

  const handleEdit = async (task) => {
    try {
      const id = taskServerId(task);
      const full = await tasksService.getById(id);
      const t = (full && full.data) ? full.data : (full || task);

      let storedPayment = null;

      try {
        const paymentResult = await taskPaymentsService.getForTask(id);
        storedPayment = paymentResult.payment || null;
      } catch (paymentError) {
        console.error(
          'Error loading task payment:',
          paymentError
        );
      }

      setEditingTask({
        ...t,
        payment: storedPayment,
      });

      const dueDate = taskDueDateForForm(t);
      let taskStatus = t.status || 'in_progress';
      if (taskStatus === 'pending' || taskStatus === 'cancelled') taskStatus = 'in_progress';
      setFormData({
        title: t.title,
        description: t.description || '',
        client_id: t.client_id || '',
        object_id: t.object_id || '',
        due_date: dueDate,
        ...taskScheduleFormFields(t),
        priority: t.priority || 'medium',
        cost: t.cost || 0,
        category: t.category || '',
        color: t.color || TASK_COLORS[1].value,
        is_recurring: t.is_recurring || false,
        recurrence_type: t.recurrence_type || '',
        address: t.address || '',
        carry_items: Array.isArray(t.carry_items) ? t.carry_items : [],
        status: taskStatus,
        expenses: Array.isArray(t.expenses) ? t.expenses.map(e => ({ amount: e.amount, category: e.category, description: e.description || '' })) : [],
        is_personal: isPersonalTask(t),
      });
      setShowModal(true);
    } catch (err) {
      console.error('Error loading task details', err);
    }
  };

  useEffect(() => {
    const linkedTaskId = searchParams.get('task_id');
    if (!linkedTaskId || linkedTaskHandled.current) return;
    linkedTaskHandled.current = true;
    void handleEdit({ id: linkedTaskId });
    setSearchParams({}, { replace: true });
    // Открытие конкретной заявки выполняется один раз при переходе из объекта.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams]);

  const handleDeleteFromForm = async (id) => {
    try {
      const taskId = editingTask ? taskServerId(editingTask) : id;
      await tasksService.delete(taskId);
      toast.success('Задача удалена');
      setShowModal(false);
      setEditingTask(null);
      resetForm();
      void loadTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error(error.response?.data?.error || 'Ошибка при удалении задачи');
    }
  };

  const handleComplete = async (id) => {
    setCompletingTaskId(id);
    setShowModal(false);
    setEditingTask(null);
    resetForm();
    setTimeout(async () => {
      try {
        const response = await tasksService.complete(id);
        const resData = (response && response.data) ? response.data : (response || {});
        const message = resData.message || 'Задача выполнена!';
        const amountDue = Number(resData.amount_due || 0);
        toast.success(`${message}${amountDue > 0 ? ` К оплате: ${amountDue} ₽` : ''}`, { autoClose: 3000 });
        setTasks((prev) => prev.map((t) => ((t.id === id || t.server_id === id) ? { ...t, status: 'completed', completed_at: new Date().toISOString(), is_paid: false, payment_status: amountDue > 0 ? 'unpaid' : 'not_applicable' } : t)));
        void loadTasks();
      } catch (error) {
        console.error('Error completing task:', error);
        toast.error(error.response?.data?.error || 'Ошибка при завершении задачи');
      } finally {
        setCompletingTaskId(null);
      }
    }, 450);
  };

  const handleReopen = async () => {
    if (!editingTask) return;
    try {
      await tasksService.reopen(taskServerId(editingTask));
      toast.success('Задача возвращена в работу');
      setShowModal(false);
      setEditingTask(null);
      resetForm();
      void loadTasks();
    } catch (error) {
      console.error('Error reopening task:', error);
      toast.error(error.response?.data?.error || 'Ошибка при возврате задачи в работу');
    }
  };

  const handleStartWaiting = (task = editingTask) => {
    if (task) setWaitingTask(task);
  };

  const handleSaveWaiting = async (payload) => {
    if (!waitingTask) return;
    setWaitingSaving(true);
    try {
      const result = await tasksService.wait(taskServerId(waitingTask), payload);
      const updated = result.task || { ...waitingTask, ...payload, status: 'waiting' };
      setTasks(prev => prev.map(task => taskServerId(task) === taskServerId(waitingTask) ? { ...task, ...updated } : task));
      setEditingTask(prev => prev && taskServerId(prev) === taskServerId(waitingTask)
        ? { ...prev, ...updated, history: result.history_event ? [result.history_event, ...(prev.history || [])] : prev.history }
        : prev);
      setFormData(prev => ({ ...prev, status: 'waiting' }));
      setWaitingTask(null);
      toast.success(result.message || 'Задача переведена в ожидание');
      void loadTasks();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось отложить задачу');
    } finally {
      setWaitingSaving(false);
    }
  };

  const handleResumeWaiting = async () => {
    if (!editingTask) return;
    try {
      const result = await tasksService.resume(taskServerId(editingTask));
      const updated = result.task || { ...editingTask, status: 'in_progress' };
      setEditingTask(prev => ({
        ...prev,
        ...updated,
        history: result.history_event ? [result.history_event, ...(prev.history || [])] : prev.history,
      }));
      setFormData(prev => ({ ...prev, status: 'in_progress' }));
      setTasks(prev => prev.map(task => taskServerId(task) === taskServerId(editingTask) ? { ...task, ...updated } : task));
      toast.success(result.message || 'Задача возвращена в работу');
      void loadTasks();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось вернуть задачу в работу');
    }
  };

  const handleAddHistory = async (comment) => {
    if (!editingTask) return null;
    try {
      const event = await tasksService.addHistory(taskServerId(editingTask), comment);
      setEditingTask(prev => ({ ...prev, history: [event, ...(prev.history || [])] }));
      toast.success('Заметка добавлена');
      return event;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось добавить заметку');
      throw error;
    }
  };

  const handleMarkPaid = async (taskId, paymentDate = null) => {
    const id = taskId || (editingTask ? taskServerId(editingTask) : null);
    if (!id) return;
    try {
      const response = await tasksService.markPaid(id, paymentDate);
      const resData = (response && response.data) ? response.data : (response || {});
      const income = resData.income || (editingTask ? editingTask.cost : 0) || 0;
      toast.success(`Задача отмечена как оплаченная${income > 0 ? ` (+${income} ₽)` : ''}`);
      setEditingTask(prev => (prev ? { ...prev, is_paid: true, payment_status: 'paid' } : prev));
      setTasks(prev => prev.map(task => (taskServerId(task) === id ? { ...task, is_paid: true, payment_status: 'paid' } : task)));
      void loadTasks();
    } catch (error) {
      console.error('Error marking task as paid:', error);
      toast.error(error.response?.data?.error || 'Ошибка при отметке задачи как оплаченной');
    }
  };

  const editingTaskId = editingTask
    ? taskServerId(editingTask)
    : null;
  const editingPaymentId = editingTask?.payment?.id || null;
  const editingPaymentStatus = editingTask?.payment?.status ?? null;
  const editingPaymentFinanceId =
    editingTask?.payment?.finance_id || null;
  const editingPaymentProcessed =
    Number(editingPaymentStatus) === 1
    && Boolean(editingPaymentFinanceId);

  useEffect(() => {
    if (
      !showModal
      || !editingPaymentId
      || editingPaymentProcessed
      || [3, 4].includes(Number(editingPaymentStatus))
    ) {
      return undefined;
    }

    let cancelled = false;
    let checking = false;

    const checkPayment = async () => {
      if (checking || cancelled) return;

      checking = true;

      try {
        const result = await taskPaymentsService.check(editingPaymentId);
        const updatedPayment = result.payment;

        if (!updatedPayment || cancelled) return;

        if (Number(updatedPayment.status) === 1) {
          const completedAt = new Date().toISOString();

          setEditingTask((previous) => (
            previous
            && previous.payment?.id === updatedPayment.id
              ? {
                  ...previous,
                  status: 'completed',
                  completed_at:
                    previous.completed_at || completedAt,
                  is_paid: true,
                  payment_status: 'paid',
                  payment: updatedPayment,
                }
              : previous
          ));

          setFormData((previous) => ({
            ...previous,
            status: 'completed',
          }));

          setTasks((previous) => previous.map((item) => (
            taskServerId(item) === editingTaskId
              ? {
                  ...item,
                  status: 'completed',
                  completed_at:
                    item.completed_at || completedAt,
                  is_paid: true,
                  payment_status: 'paid',
                }
              : item
          )));

          toast.success(
            `Оплата получена: ${Number(updatedPayment.amount || 0)
              .toLocaleString('ru-RU')} ₽`
          );

          void loadTasks();
        } else {
          setEditingTask((previous) => (
            previous
            && previous.payment?.id === updatedPayment.id
              ? {
                  ...previous,
                  payment: updatedPayment,
                }
              : previous
          ));
        }
      } catch (error) {
        console.error('Payment status check error:', error);
      } finally {
        checking = false;
      }
    };

    void checkPayment();

    const intervalId = window.setInterval(
      checkPayment,
      5000
    );

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    showModal,
    editingTaskId,
    editingPaymentId,
    editingPaymentStatus,
    editingPaymentFinanceId,
    editingPaymentProcessed,
    loadTasks,
  ]);

  const handleCreatePayment = async (
    task = editingTask,
    paymentDetails = {}
  ) => {
    if (!task) return;

    const id = taskServerId(task);
    const workAmount = roundMoney2(
      Number(paymentDetails.workAmount || 0)
    );
    const equipmentAmount = roundMoney2(
      Number(paymentDetails.equipmentAmount || 0)
    );
    const amount = roundMoney2(
      workAmount + equipmentAmount
    );

    if (!id) {
      toast.error('Не удалось определить ID задачи');
      return false;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Укажи стоимость работы или оборудования');
      return false;
    }

    const normalizedTitle = String(
      paymentDetails.title || task.title || ''
    ).trim();

    if (!normalizedTitle) {
      toast.error('Название услуги не может быть пустым');
      return false;
    }

    setPaymentCreatingTaskId(id);

    try {
      const result = await taskPaymentsService.createForTask(
        id,
        normalizedTitle,
        {
          workAmount,
          equipmentAmount,
        }
      );

      const payment = result.payment;

      if (!payment) {
        throw new Error('CRM не вернула данные платежа');
      }

      setEditingTask((previous) => (
        previous && taskServerId(previous) === id
          ? { ...previous, payment }
          : previous
      ));

      const paymentLink = payment.sbp_url || payment.payment_url;

      if (paymentLink && navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(paymentLink);
        } catch (clipboardError) {
          console.warn('Не удалось скопировать ссылку:', clipboardError);
        }
      }

      toast.success(
        result.reused
          ? 'Активный платёж уже существовал. Ссылка скопирована.'
          : 'Платёж создан. Ссылка скопирована.'
      );

      void loadTasks();
      return true;
    } catch (error) {
      console.error('Create task payment error:', error);

      toast.error(
        error.response?.data?.error ||
        error.message ||
        'Не удалось создать платёж'
      );
      return false;
    } finally {
      setPaymentCreatingTaskId(null);
    }
  };

  const handleCheckboxComplete = async (e, task) => {
    e.preventDefault();
    e.stopPropagation();
    const id = taskServerId(task);
    setCompletingTaskId(id);
    setTimeout(async () => {
      try {
        await tasksService.complete(id);
        toast.success('Задача выполнена');
        setTasks((prev) => prev.map((t) => ((t.id === id || t.server_id === id) ? { ...t, status: 'completed', completed_at: new Date().toISOString(), is_paid: false, payment_status: Number(t.cost || 0) > 0 ? 'unpaid' : 'not_applicable' } : t)));
        await loadTasks();
      } catch (error) {
        if (error.queuedForSync) {
          toast.success('Задача отмечена выполненной и будет синхронизирована с сервером');
          const local = await tasksService.getLocalTasks();
          setTasks(local || []);
        } else {
          console.error('Error completing task:', error);
          toast.error(error.response?.data?.error || 'Ошибка при завершении задачи');
        }
      } finally {
        setCompletingTaskId(null);
      }
    }, 450);
  };

  const openAddTask = (defaultDue) => {
    setEditingTask(null);
    let initialDue = '';
    if (defaultDue === 'today') {
      const d = new Date();
      initialDue = buildTaskDueDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`, false);
    } else if (defaultDue === 'tomorrow') {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      initialDue = buildTaskDueDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`, false);
    } else if (defaultDue === 'later') {
      const d = new Date();
      d.setDate(d.getDate() + 3);
      initialDue = buildTaskDueDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`, false);
    }
    setFormData(prev => ({ ...prev, ...DEFAULT_TASK_SCHEDULE, due_date: initialDue }));
    setShowModal(true);
  };

  const renderTaskRow = (task) => {
    const cost = roundMoney2(Number(task.cost));
    const personal = isPersonalTask(task);
    const hasCost = !personal && cost > 0;
    const isLater = task.due_date && getDateKey(task.due_date) > tomorrowKey;
    return (
      <div
        key={task.id}
        className={`events-task-row ${personal ? 'events-task-row-personal' : ''}`}
        style={{ background: personal ? 'linear-gradient(135deg, #6D28D9, #A21CAF)' : (task.color || '#4CAF50') }}
        onClick={() => handleEdit(task)}
      >
        <div className="events-task-content">
          <span className="events-task-title">{personal ? `👤 ${task.title}` : task.title}</span>
          {isLater && task.due_date && (
            <span className="events-task-date">{formatTaskDate(task.due_date)}</span>
          )}
          {!personal && Array.isArray(task.carry_items) && task.carry_items.length > 0 && (
            <span className="events-task-date">🧰 {task.carry_items.join(', ')}</span>
          )}
        </div>
        {hasCost && (
          <span className="events-task-cost">+{cost.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        )}
        <button
          type="button"
          className="events-task-checkbox"
          onClick={(e) => handleCheckboxComplete(e, task)}
          aria-label="Выполнить задачу"
        />
      </div>
    );
  };

  const renderUnpaidTaskRow = (task) => {
    const cost = roundMoney2(Number(task.cost));
    return (
      <div key={taskServerId(task)} className="events-task-row" style={{ background: task.color || '#4CAF50' }} onClick={() => handleEdit(task)}>
        <div className="events-task-content">
          <span className="events-task-title">{task.title}</span>
          <span className="events-task-date">Завершено, не оплачено</span>
        </div>
        <span className="events-task-cost">{cost.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽</span>
        <button
          type="button"
          className="btn btn-primary"
          style={{ marginLeft: 10, padding: '7px 11px', whiteSpace: 'nowrap' }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleMarkPaid(taskServerId(task));
          }}
        >
          Оплачено
        </button>
      </div>
    );
  };

  const renderWaitingTaskRow = (task) => (
    <div key={taskServerId(task)} className={`events-task-row events-task-row-waiting ${isPersonalTask(task) ? 'events-task-row-personal' : ''}`} onClick={() => handleEdit(task)}>
      <div className="events-task-content">
        <span className="events-task-title">⏸ {isPersonalTask(task) ? '👤 ' : ''}{task.title}</span>
        <span className="events-task-date">
          {waitingReasonLabel(task.waiting_reason)}
          {task.waiting_until ? ` · до ${formatWaitingDate(task.waiting_until)}` : ''}
        </span>
        {task.waiting_comment && <span className="events-task-date">{task.waiting_comment}</span>}
      </div>
      <button
        type="button"
        className="events-task-checkbox"
        onClick={(event) => handleCheckboxComplete(event, task)}
        aria-label="Завершить задачу"
      />
    </div>
  );

  const renderSection = (title, list, addDue) => (
    <section className="events-section">
      <div className="events-section-header">
        <h3 className="events-section-title">{title}</h3>
        <button type="button" className="events-section-add" onClick={() => openAddTask(addDue)} title="Добавить">+</button>
      </div>
      <div className="events-section-body">
        {list.length === 0 ? (
          <p className="events-no-data">Нет Данных</p>
        ) : (
          list.map(renderTaskRow)
        )}
      </div>
    </section>
  );

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="events-tasks-page">
      {completingTaskId && (
        <div className="task-complete-overlay" aria-hidden="true">
          <span className="task-complete-check">✓</span>
        </div>
      )}
      <header className="events-header">
        <button type="button" className="events-back" onClick={() => navigate(-1)} aria-label="Назад">←</button>
        <h1 className="events-title">
          События / <span className="events-title-highlight">Задачи</span>
        </h1>
        <span className="events-header-spacer" />
      </header>

      <div className="events-content">
        {waitingTasks.length > 0 && (
          <section className="events-section">
            <div className="events-section-header">
              <h3 className="events-section-title">В ожидании</h3>
            </div>
            <div className="events-section-body">{waitingTasks.map(renderWaitingTaskRow)}</div>
          </section>
        )}
        {unpaidTasks.length > 0 && (
          <section className="events-section">
            <div className="events-section-header">
              <h3 className="events-section-title">Завершено, не оплачено</h3>
            </div>
            <div className="events-section-body">{unpaidTasks.map(renderUnpaidTaskRow)}</div>
          </section>
        )}
        {renderSection('Пропущенные задачи/действия', missed, null)}
        {renderSection('Сегодня', today, 'today')}
        {renderSection('Завтра', tomorrow, 'tomorrow')}
        {renderSection('Когда-нибудь', someday, null)}
        {renderSection('Позже', later, 'later')}
      </div>

      {showModal && (
        <TaskForm
          formData={formData}
          setFormData={setFormData}
          clients={clients}
          objects={objects}
          onSubmit={handleSubmit}
          onCancel={() => { setShowModal(false); setEditingTask(null); resetForm(); }}
          onComplete={handleComplete}
          onReopen={handleReopen}
          onWait={handleStartWaiting}
          onResumeWaiting={handleResumeWaiting}
          onAddHistory={handleAddHistory}
          onDelete={handleDeleteFromForm}
          onMarkPaid={handleMarkPaid}
          onCreatePayment={handleCreatePayment}
          paymentCreatingTaskId={paymentCreatingTaskId}
          editingTask={editingTask}
        />
      )}
      <TaskWaitingDialog
        task={waitingTask}
        saving={waitingSaving}
        onClose={() => !waitingSaving && setWaitingTask(null)}
        onSave={handleSaveWaiting}
      />
    </div>
  );
};

export default Tasks;
